import { useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import { API_URL, getHeaderWithAuthorization } from "api/API";
import { setTrialInfo } from "reducers/userInfo";
import { getToken } from "utils";

/**
 * Хук для обновления данных пользователя при возврате на вкладку
 * Полезно после оплаты, когда пользователь возвращается с платежной страницы
 */
export const useRevalidateOnFocus = () => {
	const dispatch = useDispatch();
	const lastFetchRef = useRef<number>(0);
	const MIN_REFETCH_INTERVAL = 5000; // Минимальный интервал между запросами

	const revalidateUserInfo = useCallback(async () => {
		const now = Date.now();
		// Предотвращаем слишком частые запросы
		if (now - lastFetchRef.current < MIN_REFETCH_INTERVAL) return;
		if (!getToken()) return;

		lastFetchRef.current = now;

		try {
			const res = await axios.get(API_URL + "v2/auth/state", {
				headers: getHeaderWithAuthorization(),
			});

			// Обновляем только trial информацию
			dispatch(
				setTrialInfo({
					days_remaining: res?.data?.days_remaining_for_trial ?? 14,
					is_payed: res?.data?.is_payed ?? false,
				})
			);
		} catch (error) {
			console.error("Failed to revalidate user info:", error);
		}
	}, [dispatch]);

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				revalidateUserInfo();
			}
		};

		const handleFocus = () => {
			revalidateUserInfo();
		};

		// Используем оба события для надежности
		document.addEventListener("visibilitychange", handleVisibilityChange);
		window.addEventListener("focus", handleFocus);

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			window.removeEventListener("focus", handleFocus);
		};
	}, [revalidateUserInfo]);

	return { revalidateUserInfo };
};

export default useRevalidateOnFocus;

